import React, { useState, useEffect } from "react";
import {
  Box, VStack, Heading, Text, useColorModeValue, Button,
  HStack, useBreakpointValue, useToast, Spinner, Divider, Tooltip,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ExamAttemptDetails, ExamAttemptScore,ExamAttemptAnalytics } from "../Common/types";
import QuestionSetViewAttempt from "../Components/QuestionSetViewAttempt";
import ScoreViewer from "../Components/ScoreViewer";
import QuestionSetAnalytics from "../Components/QuestionSetAnalytics";

const AdminScoreViewAttempt = () => {

  const [examAttemptScore, setExamAttemptScore] = useState<ExamAttemptScore | null>(null);
  const [examAttemptDetails, setExamAttemptDetails] = useState<ExamAttemptDetails>();
  const [examAttemptAnalytics, setExamAttemptAnalytics] = useState<ExamAttemptAnalytics | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"score" | "analytics" | "attempt">("score");

  const { exam_attempt_id } = useParams<{ exam_attempt_id: string }>();
  const token = localStorage.getItem("token");
  const isAuthenticated = Boolean(token);
  const navigate = useNavigate();
  const toast = useToast();

  const textColor = useColorModeValue("blue.800", "blue.50");
  const cardBgDefault = useColorModeValue("gray.100", "gray.700");
  const cardBgHover = useColorModeValue("gray.200", "gray.600");
  const cardTextColor = useColorModeValue("gray.800", "gray.100");
  const cardBorderColor = useColorModeValue("gray.300", "gray.600");

  const fetchExamScore = async () => {
    try {
      setIsLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await axios.get(
        `${apiUrl}/exam-attempt/${exam_attempt_id}/score`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExamAttemptScore(response.data);
      console.log("Exam Attempt Score", response.data);
    } catch (error) {
      toast({
        title: "Failed to fetch exam score",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExamAnalytics = async () => {
    try {
      setIsLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await axios.get(
        `${apiUrl}/exam-attempt/${exam_attempt_id}/analytics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExamAttemptAnalytics(response.data);
      console.log("Exam Attempt Analytics", response.data);
    } catch (error) {
      toast({
        title: "Failed to fetch exam analytics",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExamAttemptDetails = async () => {
    if (examAttemptDetails) {
      setViewMode("attempt");
      return;
    }
    
    setIsLoading(true);
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    try {
    const response = await axios.get(`${apiUrl}/exam-attempt/${exam_attempt_id}/post-attempt-details`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    setExamAttemptDetails(response.data);
        console.log("Exam Attempt Details", response.data);
    
    setViewMode("attempt");
    } catch (error) {
    console.error("Failed to load exam attempt listings", error);
    toast({
        title: "Failed to fetch detailed attempt",
        status: "error",
        duration: 3000,
        isClosable: true,
    });
    } finally {
    setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchExamScore();
      fetchExamAnalytics();
    }
  }, [exam_attempt_id]);


  return (
    <Box>

      <Box mx="auto" color={textColor} p={4}>
        <HStack mb={4} justifyContent="space-between">
          <Heading size="md">Exam Scores</Heading>
     
            <HStack spacing={4}>
              <Button
                colorScheme="green"
                size="sm"
                onClick={() => setViewMode("score")}
                isDisabled={viewMode === "score"}
              >
                View Score
              </Button>
            <Tooltip
              label="Analytics not generated yet."
              isDisabled={!!examAttemptAnalytics}
            >
              <Button
                colorScheme="purple"
                size="sm"
                onClick={() => setViewMode("analytics")}
                isDisabled={!examAttemptAnalytics || viewMode === "analytics"}
              >
                View Analytics
              </Button>
            </Tooltip>
              <Button
                colorScheme="blue"
                size="sm"
                onClick={() => fetchExamAttemptDetails()}
                isDisabled={viewMode === "attempt" }
              >
               View Detailed Attempt
              </Button>
            </HStack>
         
        </HStack>

        {viewMode === "score" && examAttemptScore && (
        <Box
          p={2}
          borderRadius="sm"
          boxShadow="md"
          border="1px solid"
          borderColor="gray.200"
        >
            <ScoreViewer scoreData={examAttemptScore} />
          
        </Box>
        )}

        {isLoading ? (
          <Spinner size="xl" />
        ) : (
          viewMode === "analytics" && (
                <Box
       
            borderRadius="sm"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            >
              <HStack p={4} spacing={4}  >
                {examAttemptAnalytics?.attempted_question_set_analytics.map((qs, idx) => (
                    <Box
                        key={qs.exam_attempted_question_set_id}
                        p={2}
                        px={4}
                        borderRadius="md"
                        minW="160px"
                        cursor="pointer"
                        bg={selectedIndex === idx ? "blue.600" : cardBgDefault}
                        color={selectedIndex === idx ? "white" : cardTextColor}
                        borderWidth="1px"
                        borderColor={selectedIndex === idx ? "blue.800" : cardBorderColor}
                        onClick={() => setSelectedIndex(idx)}
                        _hover={{
                            bg: selectedIndex === idx ? "blue.700" : cardBgHover,
                            transform: "translateY(-2px)",
                            boxShadow: "md"
                        }}
                        transition="all 0.2s ease"
                        textAlign="center"
                        >
                        <Text fontWeight="semibold" fontSize="sm" isTruncated>
                            {qs.title}
                        </Text>

                        </Box>

                ))}
              </HStack>
              <Divider />
              <Box position="relative" minH="500px">
                {examAttemptAnalytics?.attempted_question_set_analytics.map((qs, idx) => (
                  <Box
                    key={qs.exam_attempted_question_set_id}
                    display={selectedIndex === idx ? "block" : "none"}
                    transition="opacity 0.2s ease"
                  >
                    <QuestionSetAnalytics examAttemptedQsetAnalyticsProp={qs} />
                  </Box>
                ))}
              </Box>
            </Box>
          )
        )}

        {isLoading ? (
          <Spinner size="xl" />
        ) : (
          viewMode === "attempt" && (
                <Box
       
            borderRadius="sm"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            >
              <HStack p={4} spacing={4}  >
                {examAttemptDetails?.question_sets.map((qs, idx) => (
                    <Box
                        key={qs.exam_attempted_question_set_id}
                        p={2}
                        px={4}
                        borderRadius="md"
                        minW="160px"
                        cursor="pointer"
                        bg={selectedIndex === idx ? "blue.600" : cardBgDefault}
                        color={selectedIndex === idx ? "white" : cardTextColor}
                        borderWidth="1px"
                        borderColor={selectedIndex === idx ? "blue.800" : cardBorderColor}
                        onClick={() => setSelectedIndex(idx)}
                        _hover={{
                            bg: selectedIndex === idx ? "blue.700" : cardBgHover,
                            transform: "translateY(-2px)",
                            boxShadow: "md"
                        }}
                        transition="all 0.2s ease"
                        textAlign="center"
                        >
                        <Text fontWeight="semibold" fontSize="sm" isTruncated>
                            {qs.question_set_title}
                        </Text>
                        <Text fontSize="xs">
                            Total: {qs.attempted_question_details.length} questions
                        </Text>
                        </Box>

                ))}
              </HStack>
              <Divider />
              <Box position="relative" minH="500px">
                {examAttemptDetails?.question_sets.map((qs, idx) => (
                  <Box
                    key={qs.exam_attempted_question_set_id}
                    display={selectedIndex === idx ? "block" : "none"}
                    transition="opacity 0.2s ease"
                  >
                    <QuestionSetViewAttempt examAttemptedQuestionSetProp={qs.attempted_question_details} />
                  </Box>
                ))}
              </Box>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default AdminScoreViewAttempt;
